/**
 * Overlay 位置调整工具函数
 * 提供 placement、自动翻转和边界保护等功能
 */

const DEFAULT_PADDING = 16;
const DEFAULT_ARROW_SIZE = 8;

type TBasePlacement = 'top' | 'bottom' | 'left' | 'right';
type TAlignment = 'start' | 'end' | 'center';

const AUTO_BASE_PLACEMENTS: TBasePlacement[] = [
  'top',
  'bottom',
  'right',
  'left',
];

const OPPOSITE_PLACEMENT_MAP: Record<TBasePlacement, TBasePlacement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

const isBasePlacement = (value: string): value is TBasePlacement => (
  value === 'top' || value === 'bottom' || value === 'left' || value === 'right'
);

const isAlignment = (value?: string): value is Exclude<TAlignment, 'center'> => value === 'start' || value === 'end';

const composePlacement = (
  base: TBasePlacement,
  alignment: TAlignment
): TPlacement => {
  if (alignment === 'center') {
    return base;
  }
  return `${base}-${alignment}` as TPlacement;
};

const parsePlacement = (placement: TPlacement): {
  basePlacement: TBasePlacement | 'auto';
  alignment: TAlignment;
  isAuto: boolean;
} => {
  const [base, alignment] = placement.split('-');

  if (base === 'auto') {
    return {
      basePlacement: 'auto',
      alignment: isAlignment(alignment) ? alignment : 'center',
      isAuto: true,
    };
  }

  const basePlacement = isBasePlacement(base) ? base : 'bottom';
  const parsedAlignment = isAlignment(alignment) ? alignment : 'center';

  return {
    basePlacement,
    alignment: parsedAlignment,
    isAuto: false,
  };
};

const getOppositePlacement = (placement: TPlacement): TPlacement | null => {
  const parsed = parsePlacement(placement);

  if (parsed.isAuto || parsed.basePlacement === 'auto') {
    return null;
  }

  const oppositeBase = OPPOSITE_PLACEMENT_MAP[parsed.basePlacement];
  return composePlacement(oppositeBase, parsed.alignment);
};

const getAutoPlacements = (placement: TPlacement): TPlacement[] => {
  const { alignment } = parsePlacement(placement);
  return AUTO_BASE_PLACEMENTS.map((base) => composePlacement(base, alignment));
};

const getArrowSpace = (arrow?: { enabled: boolean; size?: number; }): number => {
  if (!arrow?.enabled) {
    return 0;
  }
  return arrow.size ?? DEFAULT_ARROW_SIZE;
};

const calculateOverflow = (
  position: { left: number; top: number; },
  overlayRect: DOMRect,
  padding: number
): { total: number; } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const right = position.left + overlayRect.width;
  const bottom = position.top + overlayRect.height;

  const overflowLeft = Math.max(padding - position.left, 0);
  const overflowTop = Math.max(padding - position.top, 0);
  const overflowRight = Math.max(right - (viewportWidth - padding), 0);
  const overflowBottom = Math.max(bottom - (viewportHeight - padding), 0);

  return {
    total: overflowLeft + overflowTop + overflowRight + overflowBottom,
  };
};

const ensureWithinViewport = (
  position: { left: number; top: number; },
  overlayRect: DOMRect,
  padding: number
): { left: number; top: number; changed: boolean; } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const minLeft = padding;
  const minTop = padding;
  const maxLeft = Math.max(
    padding,
    viewportWidth - overlayRect.width - padding
  );
  const maxTop = Math.max(
    padding,
    viewportHeight - overlayRect.height - padding
  );

  let {left} = position;
  let {top} = position;
  let changed = false;

  if (left < minLeft) {
    left = minLeft;
    changed = true;
  } else if (left > maxLeft) {
    left = maxLeft;
    changed = true;
  }

  if (top < minTop) {
    top = minTop;
    changed = true;
  } else if (top > maxTop) {
    top = maxTop;
    changed = true;
  }

  return { left, top, changed };
};

const resolvePlacement = (
  overlayRect: DOMRect,
  referenceRect: DOMRect,
  options: {
    placement: TPlacement;
    offset?: { mainAxis?: number; crossAxis?: number; };
    arrow?: { enabled: boolean; size?: number; };
    padding: number;
    flipped: boolean;
  }
): {
  placement: TPlacement;
  position: { left: number; top: number; };
  wasFlipped: boolean;
} => {
  const { placement, offset, arrow, padding, flipped } = options;

  const evaluatePlacement = (candidate: TPlacement) => {
    const position = calculatePlacementPosition(
      overlayRect,
      referenceRect,
      candidate,
      {
        offset,
        arrow,
      }
    );
    const overflow = calculateOverflow(position, overlayRect, padding);

    return { candidate, position, overflow };
  };

  const parsed = parsePlacement(placement);

  if (parsed.isAuto) {
    const candidates = getAutoPlacements(placement);
    let best = evaluatePlacement(candidates[0]);

    for (let i = 1;i < candidates.length;i += 1) {
      const current = evaluatePlacement(candidates[i]);
      if (current.overflow.total < best.overflow.total) {
        best = current;
      }
    }

    return {
      placement: best.candidate,
      position: best.position,
      wasFlipped: best.candidate !== placement,
    };
  }

  const primary = evaluatePlacement(placement);
  const allowFlip = flipped !== false;

  if (!allowFlip || primary.overflow.total === 0) {
    return {
      placement,
      position: primary.position,
      wasFlipped: false,
    };
  }

  const oppositePlacement = getOppositePlacement(placement);

  if (oppositePlacement) {
    const opposite = evaluatePlacement(oppositePlacement);
    if (opposite.overflow.total < primary.overflow.total) {
      return {
        placement: oppositePlacement,
        position: opposite.position,
        wasFlipped: true,
      };
    }
  }

  return {
    placement,
    position: primary.position,
    wasFlipped: false,
  };
};

export interface IPositionAdjustOptions {
  /** 边界内边距 */
  padding?: number;
}

export interface IPositionAdjustResult {
  /** 是否需要调整位置 */
  hasChanged: boolean;
  /** 新的 left 位置 */
  newLeft: number;
  /** 新的 top 位置 */
  newTop: number;
  /** 原始位置信息 */
  originalRect: DOMRect;
  /** 视口尺寸 */
  viewport: {
    width: number;
    height: number;
  };
}

export type TPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'auto'
  | 'auto-start'
  | 'auto-end';

export interface IPlacementAdjustOptions extends IPositionAdjustOptions {
  /** 参考元素（目标元素） */
  referenceElement: HTMLElement | null;
  /** 期望的展示位置 */
  placement?: TPlacement;
  /** 位置偏移配置 */
  offset?: {
    /** 沿 placement 主方向的偏移量 */
    mainAxis?: number;
    /** 垂直于主方向的偏移量 */
    crossAxis?: number;
  };
  /** 箭头配置 */
  arrow?: {
    /** 是否启用箭头预留空间 */
    enabled: boolean;
    /** 箭头尺寸 */
    size?: number;
  };
  /** 是否允许自动翻转 */
  flipped?: boolean;
}

export interface IPlacementAdjustResult extends IPositionAdjustResult {
  /** 最终使用的 placement */
  finalPlacement: TPlacement;
  /** 是否发生了翻转 */
  flipped: boolean;
}

/**
 * 根据 placement 计算 Overlay 的初始位置
 * @param overlayRect Overlay 当前的 DOMRect
 * @param referenceRect 参考元素的 DOMRect
 * @param placement placement 配置
 * @param options 额外配置
 */
export const calculatePlacementPosition = (
  overlayRect: DOMRect,
  referenceRect: DOMRect,
  placement: TPlacement,
  options: {
    offset?: { mainAxis?: number; crossAxis?: number; };
    arrow?: { enabled: boolean; size?: number; };
  } = {}
): { left: number; top: number; } => {
  const parsed = parsePlacement(placement);

  if (parsed.basePlacement === 'auto') {
    throw new Error('calculatePlacementPosition 需要非 auto 的 placement');
  }

  const arrowSpace = getArrowSpace(options.arrow);
  const mainAxisOffset = options.offset?.mainAxis ?? 0;
  const crossAxisOffset = options.offset?.crossAxis ?? 0;

  let left =
    referenceRect.left +
    referenceRect.width / 2 -
    overlayRect.width / 2;
  let top =
    referenceRect.top +
    referenceRect.height / 2 -
    overlayRect.height / 2;

  switch (parsed.basePlacement) {
    case 'top':
      top = referenceRect.top - overlayRect.height - arrowSpace;
      break;
    case 'bottom':
      top = referenceRect.bottom + arrowSpace;
      break;
    case 'left':
      left = referenceRect.left - overlayRect.width - arrowSpace;
      break;
    case 'right':
      left = referenceRect.right + arrowSpace;
      break;
    default:
      break;
  }

  if (parsed.basePlacement === 'top' || parsed.basePlacement === 'bottom') {
    if (parsed.alignment === 'start') {
      left = referenceRect.left;
    } else if (parsed.alignment === 'end') {
      left = referenceRect.right - overlayRect.width;
    }
  } else if (parsed.basePlacement === 'left' || parsed.basePlacement === 'right') {
    if (parsed.alignment === 'start') {
      top = referenceRect.top;
    } else if (parsed.alignment === 'end') {
      top = referenceRect.bottom - overlayRect.height;
    }
  }

  switch (parsed.basePlacement) {
    case 'top':
      top -= mainAxisOffset;
      left += crossAxisOffset;
      break;
    case 'bottom':
      top += mainAxisOffset;
      left += crossAxisOffset;
      break;
    case 'left':
      left -= mainAxisOffset;
      top += crossAxisOffset;
      break;
    case 'right':
      left += mainAxisOffset;
      top += crossAxisOffset;
      break;
    default:
      break;
  }

  return { left, top };
};

/**
 * 检查并计算需要调整的位置
 * @param element 需要调整位置的 DOM 元素
 * @param options 调整选项
 * @returns 位置调整结果
 */
export const checkPositionAdjustment = (
  element: HTMLElement,
  options: IPositionAdjustOptions = {}
): IPositionAdjustResult => {
  const padding = options.padding ?? DEFAULT_PADDING;

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let newLeft = rect.left;
  let newTop = rect.top;
  let hasChanged = false;

  if (rect.right > viewportWidth - padding) {
    newLeft = viewportWidth - rect.width - padding;
    hasChanged = true;
  }

  if (rect.bottom > viewportHeight - padding) {
    newTop = viewportHeight - rect.height - padding;
    hasChanged = true;
  }

  if (newLeft < padding) {
    newLeft = padding;
    hasChanged = true;
  }

  if (newTop < padding) {
    newTop = padding;
    hasChanged = true;
  }

  const result: IPositionAdjustResult = {
    hasChanged,
    newLeft,
    newTop,
    originalRect: rect,
    viewport: {
      width: viewportWidth,
      height: viewportHeight,
    },
  };

  return result;
};

/**
 * 应用位置调整
 * @param element 需要调整位置的 DOM 元素
 * @param result 位置调整结果
 */
export const applyPositionAdjustment = (
  element: HTMLElement,
  result: IPositionAdjustResult
): void => {
  if (result.hasChanged) {
    element.style.left = `${result.newLeft}px`;
    element.style.top = `${result.newTop}px`;
    element.style.transform = 'none';
  }
};

/**
 * 检查并应用位置调整（一步完成）
 * @param element 需要调整位置的 DOM 元素
 * @param options 调整选项
 * @returns 位置调整结果
 */
export const adjustOverlayPosition = (
  element: HTMLElement,
  options: IPositionAdjustOptions = {}
): IPositionAdjustResult => {
  const result = checkPositionAdjustment(element, options);
  applyPositionAdjustment(element, result);
  return result;
};

/**
 * 根据 placement 调整 Overlay 位置
 * @param overlay 需要调整的 Overlay 元素
 * @param options 调整配置
 */
export const adjustOverlayPositionByPlacement = (
  overlay: HTMLElement,
  options: IPlacementAdjustOptions
): IPlacementAdjustResult => {

  const {
    referenceElement,
    placement = 'bottom-start',
    offset = { mainAxis: 0, crossAxis: 0 },
    arrow = { enabled: false, size: DEFAULT_ARROW_SIZE },
    flipped = true,
    padding = DEFAULT_PADDING,
  } = options;


  if (!(overlay instanceof HTMLElement) || !(referenceElement instanceof HTMLElement)) {
    return {
      hasChanged: false,
      newLeft: 0,
      newTop: 0,
      originalRect: overlay?.getBoundingClientRect(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      finalPlacement: 'bottom-start',
      flipped: false,
    };
  }


  const overlayRect = overlay.getBoundingClientRect();
  const referenceRect = referenceElement.getBoundingClientRect();

  if (
    !Number.isFinite(overlayRect.width) || !Number.isFinite(overlayRect.height) ||
    overlayRect.width === 0 || overlayRect.height === 0 ||
    !Number.isFinite(referenceRect.width) || !Number.isFinite(referenceRect.height) ||
    referenceRect.width === 0 || referenceRect.height === 0
  ) {
    return {
      hasChanged: false,
      newLeft: overlayRect.left,
      newTop: overlayRect.top,
      originalRect: overlayRect,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      finalPlacement: placement,
      flipped: false,
    };
  }

  const placementResolution = resolvePlacement(overlayRect, referenceRect, {
    placement,
    offset,
    arrow,
    padding,
    flipped,
  });
  const viewportAdjustment = ensureWithinViewport(
    placementResolution.position,
    overlayRect,
    padding
  );

  let finalLeft = viewportAdjustment.left;
  let finalTop = viewportAdjustment.top;

  if (!Number.isFinite(finalLeft) || !Number.isFinite(finalTop)) {
    finalLeft = overlayRect.left;
    finalTop = overlayRect.top;
  }

  const hasChanged =
    viewportAdjustment.changed ||
    finalLeft !== overlayRect.left ||
    finalTop !== overlayRect.top;

  overlay.style.left = `${finalLeft}px`;
  overlay.style.top = `${finalTop}px`;
  overlay.style.transform = 'none';

  const result: IPlacementAdjustResult = {
    hasChanged,
    newLeft: finalLeft,
    newTop: finalTop,
    originalRect: overlayRect,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    finalPlacement: placementResolution.placement,
    flipped: placementResolution.wasFlipped,
  };

  return result;
};

/**
 * 创建位置调整器实例
 * 提供更高级的功能，如防抖、批量调整等
 */
export class OverlayPositionAdjuster {
  private rafRef: number | null = null;

  private resizeObserver: ResizeObserver | null = null;

  private elements: Set<HTMLElement> = new Set();

  private options: IPositionAdjustOptions;

  private placementConfigs: Map<HTMLElement, IPlacementAdjustOptions> = new Map();

  constructor(options: IPositionAdjustOptions = {}) {
    this.options = options;
  }

  /**
   * 添加需要监听位置调整的元素
   * @param element DOM 元素
   */
  addElement(element: HTMLElement): void {
    this.elements.add(element);
    this.setupResizeObserver();
    this.observeElementWithReferences(element);
  }

  /**
   * 添加带 placement 配置的元素
   * @param element Overlay 元素
   * @param placementOptions placement 配置
   */
  addElementWithPlacement(
    element: HTMLElement,
    placementOptions: IPlacementAdjustOptions
  ): void {
    this.elements.add(element);
    this.placementConfigs.set(element, placementOptions);
    this.setupResizeObserver();
    this.observeElementWithReferences(element, placementOptions);
  }

  /**
   * 移除监听位置调整的元素
   * @param element DOM 元素
   */
  removeElement(element: HTMLElement): void {
    const placementOptions = this.placementConfigs.get(element);
    this.unobserveElementWithReferences(element, placementOptions ?? undefined);
    this.elements.delete(element);
    this.placementConfigs.delete(element);
    if (this.elements.size === 0) {
      this.cleanup();
    }
  }

  /**
   * 手动调整所有元素的位置
   */
  adjustAll(): void {
    this.elements.forEach((element) => {
      const placementOptions = this.placementConfigs.get(element);
      if (placementOptions) {
        const resolvedOptions: IPlacementAdjustOptions = {
          ...placementOptions,
          padding:
            placementOptions.padding ?? this.options.padding ?? DEFAULT_PADDING,
        };
        adjustOverlayPositionByPlacement(element, resolvedOptions);
      } else {
        adjustOverlayPosition(element, this.options);
      }
    });
  }

  /**
   * 手动调整指定元素的位置
   * @param element DOM 元素
   */
  adjustElement(element: HTMLElement): IPositionAdjustResult {
    const placementOptions = this.placementConfigs.get(element);
    if (placementOptions) {
      const resolvedOptions: IPlacementAdjustOptions = {
        ...placementOptions,
        padding:
          placementOptions.padding ?? this.options.padding ?? DEFAULT_PADDING,
      };
      return adjustOverlayPositionByPlacement(element, resolvedOptions);
    }
    return adjustOverlayPosition(element, this.options);
  }

  /**
   * 基于 placement 调整指定元素的位置
   * @param element DOM 元素
   * @param placementOptions placement 配置
   */
  adjustElementByPlacement(
    element: HTMLElement,
    placementOptions?: IPlacementAdjustOptions
  ): IPlacementAdjustResult {
    if (placementOptions) {
      this.placementConfigs.set(element, placementOptions);
      this.setupResizeObserver();
      this.observeElementWithReferences(element, placementOptions);
    }

    const storedOptions = this.placementConfigs.get(element);

    if (!storedOptions) {
      throw new Error('未找到该元素的 placement 配置');
    }

    const resolvedOptions: IPlacementAdjustOptions = {
      ...storedOptions,
      padding: storedOptions.padding ?? this.options.padding ?? DEFAULT_PADDING,
    };

    return adjustOverlayPositionByPlacement(element, resolvedOptions);
  }

  /**
   * 检查指定元素是否需要位置调整
   * @param element DOM 元素
   */
  checkElement(element: HTMLElement): IPositionAdjustResult {
    return checkPositionAdjustment(element, this.options);
  }

  /**
   * 销毁调整器实例
   */
  destroy(): void {
    this.cleanup();
    this.elements.clear();
    this.placementConfigs.clear();
  }

  /**
   * 设置 ResizeObserver 监听
   */
  private setupResizeObserver(): void {
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleAdjustment();
      });
    }

    this.elements.forEach((element) => {
      const placementOptions = this.placementConfigs.get(element);
      this.observeElementWithReferences(element, placementOptions ?? undefined);
    });
  }

  private observeElementWithReferences(
    element: HTMLElement,
    placementOptions?: IPlacementAdjustOptions
  ): void {
    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.observe(element);

    const options = placementOptions ?? this.placementConfigs.get(element);
    if (options) {
      this.resizeObserver.observe(options.referenceElement);
    }
  }

  private unobserveElementWithReferences(
    element: HTMLElement,
    placementOptions?: IPlacementAdjustOptions
  ): void {
    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.unobserve(element);

    if (placementOptions) {
      this.resizeObserver.unobserve(placementOptions.referenceElement);
    }
  }

  /**
   * 使用 requestAnimationFrame 调度位置调整
   */
  private scheduleAdjustment(): void {
    if (this.rafRef) {
      cancelAnimationFrame(this.rafRef);
    }
    this.rafRef = requestAnimationFrame(() => {
      this.adjustAll();
    });
  }

  /**
   * 清理资源
   */
  private cleanup(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.rafRef) {
      cancelAnimationFrame(this.rafRef);
      this.rafRef = null;
    }
  }
}

/**
 * 创建位置调整器实例的便捷函数
 * @param options 调整选项
 * @returns 位置调整器实例
 */
export const createPositionAdjuster = (
  options: IPositionAdjustOptions = {}
): OverlayPositionAdjuster => new OverlayPositionAdjuster(options);

